import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { ConfigurationService } from '@ciam/library';
import { BypassAssetsInterceptor } from '../bypass-assets/bypass.assets.interceptor';
import { QueryParamService } from '../../services/query-param/query-param.service';

/**
 * Intercept backend requests and interpolates the ${api_host} parameter with either the legit host or a mocked host based on
 * configuration {mock: boolean} or a query parameter '?mock=no|off|false'.
 */

@Injectable()
export class MockHostInterceptor extends BypassAssetsInterceptor {

  constructor(
    private config: ConfigurationService,
    private queryParamService: QueryParamService) {
    super();
  }

  public interceptInternal(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const reqUrl = req.url ?? '';

    // Use dict$ (async) so this interceptor waits for config to finish loading
    // before constructing the host URL. config.select() (synchronous) returns
    // undefined when called before config.json / oauth.json have been fetched,
    // which previously produced requests to 'https://undefined/<path>'.
    return this.config.dict$({
      envHost: 'env.host',
      oauthHost: 'oauth.host',
      mockHost: 'env.mock_host',
      mock: 'env.mock'
    }).pipe(
      take(1),
      switchMap(({ envHost, oauthHost, mockHost, mock }) => {
        const isMock = this.queryParamService.getFromWindowRef('mock') === 'true' || !!mock;
        const rawHost = reqUrl.includes('adminCal') ? envHost : oauthHost;
        const host = isMock ? mockHost : (rawHost ? `https://${rawHost}` : '');
        const url = `${host}${reqUrl}`;
        return next.handle(req.clone({ url }));
      })
    );
  }

  /**
   * Determine if mocks are enabled/disabled based on configuration or query parameter.  Query params take precedence.
   *
   * @return true if mocks are enabled, false otherwise
   */
  get isMock(): boolean {
    if(this.queryParamService.getFromWindowRef('mock') === 'true' ) {
      return true;
    }
    else if(this.config.select<boolean>('env.mock')) { //DO NOT USE INTELLIJ SIMPLIFY HERE. This can return null/undefined
      return true;
    }
    else {
      return false;
    }
  }
}
